import React, { useState, useEffect, useRef } from 'react';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Header from '../../shared/header/Header';
import './FullMapPage.css';

const MarcaPoligonos = () => {
  const { id } = useParams();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [map, setMap] = useState(null);
  const [mapData, setMapData] = useState(null);
  const infoWindowRef = useRef(null);

  useEffect(() => {
    if (window.google) {
      initMap();
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD1Nl9yulU0jgl6VRGkK89neIalMrblrws&libraries=geometry&callback=initMap`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      window.initMap = initMap;
    }

    return () => {
      if (window.initMap === initMap) {
        window.initMap = undefined;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (map) {
      fetchMapDetails();
    }
  }, [map]);

  const initMap = () => {
    const mapInstance = new window.google.maps.Map(document.getElementById('map'), {
      center: { lat: -34.6037, lng: -58.3816 },
      zoom: 12,
    });
    setMap(mapInstance);
    infoWindowRef.current = new window.google.maps.InfoWindow({
      maxWidth: 200 // Tamaño máximo del tooltip
    });
  };

  const fetchMapDetails = async () => {
    try {
      const response = await axios.get(`https://mapa.ne3publicidad.com.ar/api/api/getMap/${id}`);
      const data = response.data;
      setMapData(data);
      drawMapElements(data);
    } catch (error) {
      console.error('Error fetching map details:', error);
      showAlert('Error al cargar los detalles del mapa', 'error');
    }
  };

  const drawMapElements = (data) => {
    if (!map || !data) return;

    // Dibujar líneas con tooltips minimalistas
    if (data.lineas && data.lineas.length > 0) {
      const linesByColor = {};
      
      data.lineas.forEach(line => {
        if (!linesByColor[line.color]) {
          linesByColor[line.color] = {};
        }
        if (!linesByColor[line.color][line.linea]) {
          linesByColor[line.color][line.linea] = [];
        }
        linesByColor[line.color][line.linea].push({
          lat: parseFloat(line.lat),
          lng: parseFloat(line.lng)
        });
      });

      Object.keys(linesByColor).forEach(color => {
        Object.keys(linesByColor[color]).forEach(lineName => {
          const polyline = new window.google.maps.Polyline({
            path: linesByColor[color][lineName],
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 1.0,
            strokeWeight: 4,
            map: map
          });

          // Tooltip minimalista solo con el nombre
          polyline.addListener('mouseover', (event) => {
            infoWindowRef.current.setContent(`<div class="simple-tooltip">${lineName}</div>`);
            infoWindowRef.current.setPosition(event.latLng);
            infoWindowRef.current.open(map);
          });

          polyline.addListener('mouseout', () => {
            infoWindowRef.current.close();
          });
        });
      });
    }

    // Dibujar polígonos con tooltips minimalistas
    if (data.poligonos && data.poligonos.length > 0) {
      const polygonsByColor = {};
      
      data.poligonos.forEach(polygonGroup => {
        if (Array.isArray(polygonGroup)) {
          polygonGroup.forEach(point => {
            if (!polygonsByColor[point.color]) {
              polygonsByColor[point.color] = {};
            }
            if (!polygonsByColor[point.color][point.nombre_zona]) {
              polygonsByColor[point.color][point.nombre_zona] = [];
            }
            polygonsByColor[point.color][point.nombre_zona].push({
              lat: parseFloat(point.lat),
              lng: parseFloat(point.lng),
              punto_orden: point.punto_orden
            });
          });
        }
      });

      Object.keys(polygonsByColor).forEach(color => {
        Object.keys(polygonsByColor[color]).forEach(polygonName => {
          const points = polygonsByColor[color][polygonName];
          if (points[0]?.punto_orden) {
            points.sort((a, b) => a.punto_orden - b.punto_orden);
          }
          
          const polygon = new window.google.maps.Polygon({
            paths: points,
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: color,
            fillOpacity: 0.35,
            map: map
          });

          // Calcular centro para posicionar el tooltip
          const bounds = new window.google.maps.LatLngBounds();
          points.forEach(point => bounds.extend(new window.google.maps.LatLng(point.lat, point.lng)));
          const center = bounds.getCenter();

          // Tooltip minimalista solo con el nombre
          polygon.addListener('mouseover', () => {
            infoWindowRef.current.setContent(`<div class="simple-tooltip">${polygonName}</div>`);
            infoWindowRef.current.setPosition(center);
            infoWindowRef.current.open(map);
          });

          polygon.addListener('mouseout', () => {
            infoWindowRef.current.close();
          });
        });
      });
    }

    // Dibujar puntos de interés con tooltips más completos
    if (data.puntosInteres && data.puntosInteres.length > 0) {
      data.puntosInteres.forEach(point => {
        if (point.lat && point.lng) {
          const marker = new window.google.maps.Marker({
            position: { 
              lat: parseFloat(point.lat), 
              lng: parseFloat(point.lng) 
            },
            map: map,
            title: point.nombre || 'Punto de Interés',
            icon: {
              url: point.icono || 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
              scaledSize: new window.google.maps.Size(32, 32)
            }
          });

          // Tooltip más detallado para marcadores
          const content = `
            <div class="marker-tooltip">
              <h3>${point.nombre || 'Punto de Interés'}</h3>
              ${point.direccion ? `<p><strong>Dirección:</strong> ${point.direccion}</p>` : ''}
              ${point.localidad ? `<p><strong>Localidad:</strong> ${point.localidad}</p>` : ''}
            </div>
          `;

          marker.addListener('mouseover', () => {
            infoWindowRef.current.setContent(content);
            infoWindowRef.current.open(map, marker);
          });

          marker.addListener('mouseout', () => {
            infoWindowRef.current.close();
          });
        }
      });
    }
  };

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  return (
    <div className="container">
      <Header />
      <div id="map" className="map"></div>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MarcaPoligonos;